import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useState } from 'react';
import { intakes, type ExtractedIntakeData, type ExtractedBoreholeData } from '@/api/endpoints';
import { validateUploadSize } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';
import { Icon } from './Icon';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Callback po akceptacji ekstrakcji — parent (IntakeWizard) używa do pre-fillu pól i layers. */
  onApply: (data: ExtractedIntakeData) => void;
}

/** Akceptacja per sekcja per otwór. Pozwala odrzucić np. tylko hydraulikę. */
interface BoreholeAcceptance {
  basic: boolean;       // designation, function, depth, diameter, yield, ...
  construction: boolean; // housing, filter, pump, meter
  hydraulics: boolean;  // pumping test, k, T
  layers: boolean;      // profil litologiczny
}

/**
 * Modal upload + AI extraction → step-by-step approval. Każda sekcja danych ma osobny
 * checkbox akceptacji. Geolog może np. wziąć tylko dane ujęcia + profil litologiczny,
 * a odrzucić konstrukcję otworu jeśli AI źle przepisał z karty otworu.
 */
export function IntakeDocumentExtractModal({ open, onClose, onApply }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<ExtractedIntakeData | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);   // null = brak wysyłki

  // Stan akceptacji — odświeżany przy każdym nowym preview
  const [acceptIntake, setAcceptIntake] = useState(true);
  const [boreholeAcc, setBoreholeAcc] = useState<BoreholeAcceptance[]>([]);

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { file, preview, acceptIntake, boreholeAcc }, onClose, open, busy,
  });

  if (!open) return null;

  const extract = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { setErr('Wybierz plik (PDF/DOCX/skan)'); return; }
    // Limit 100 MB na plik — blokujemy przed wysyłką.
    const sizeErr = validateUploadSize(file);
    if (sizeErr) { setErr(sizeErr); return; }
    setBusy(true); setErr(null); setPreview(null); setUploadPct(0);
    try {
      const data = await intakes.extractFromDocument(file, p => setUploadPct(p.percent));
      setPreview(data);
      setAcceptIntake(true);
      setBoreholeAcc(data.boreholes.map(() => ({
        basic: true, construction: true, hydraulics: true, layers: true,
      })));
      if (data.extractedFields.length === 0) {
        setErr('AI nie znalazł danych ujęcia w tym dokumencie. Sprawdź czy plik to operat / dokumentacja / decyzja.');
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false); setUploadPct(null);
    }
  };

  const apply = () => {
    if (!preview) return;
    // Filtruj zgodnie z checkboxami — zerujemy pola sekcji, których user nie zaakceptował.
    const filtered: ExtractedIntakeData = {
      ...preview,
      // Dane ujęcia: jeśli odrzucone, zerujemy wszystkie poza listą otworów i extractedFields.
      ...(acceptIntake ? {} : {
        name: null, town: null, county: null, voivodeship: null,
        latitude: null, longitude: null, qM3h: null,
        jcwpdCode: null, jcwpdName: null, regionWodny: null, dorzecze: null,
        approvedResourceM3h: null, approvedResourceDecisionRef: null,
      }),
      boreholes: preview.boreholes
        .map((b, i) => filterBorehole(b, boreholeAcc[i]))
        .filter((b): b is ExtractedBoreholeData => b !== null),
    };
    onApply(filtered);
  };

  const reset = () => {
    setFile(null); setPreview(null); setErr(null); setBoreholeAcc([]);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div onClick={e => e.stopPropagation()}
           className="bg-white rounded-lg shadow-xl w-full max-w-[920px] max-h-[92vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100">
          <h2 className="text-[15px] font-semibold text-ink-900 flex items-center gap-2">
            <Icon name="sparkles" className="w-4 h-4 text-ai-600"/>
            Utwórz ujęcie z dokumentu (AI)
          </h2>
          <div className="text-[11px] text-ink-500 mt-1">
            Wgraj operat, dokumentację hydrogeologiczną lub decyzję. AI wyciągnie dane ujęcia + otwory
            (profil litologiczny, konstrukcja, hydraulika). Zatwierdzasz w krokach.
          </div>
        </div>

        {!preview && (
          <form onSubmit={extract} className="p-5 grid gap-3">
            <label className="field">
              <span className="field-label">Plik dokumentu *</span>
              <input type="file" accept="application/pdf,.docx,.doc,image/*,.txt"
                     onChange={e => setFile(e.target.files?.[0] ?? null)}
                     disabled={busy} className="input"/>
              {file && (
                <span className="text-[11px] text-ink-500 mt-1">
                  {file.name} · {(file.size / (1024 * 1024)).toFixed(1)} MB
                </span>
              )}
              <span className="text-[10px] text-ink-400 mt-1">
                Obsługiwane: PDF, DOCX, DOC, PNG, JPG (OCR dla obrazów i skanów), TXT. Max ~250 MB.
              </span>
            </label>
            {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}
            {uploadPct !== null && (
              <UploadProgressBar percent={uploadPct} label="Wysyłanie i analiza…"/>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={requestClose} disabled={busy} className="btn btn-secondary">Anuluj</button>
              <button type="submit" disabled={busy || !file} className="btn btn-primary">
                <Icon name="sparkles" className="w-3.5 h-3.5"/>
                {busy ? 'AI analizuje dokument…' : 'Wyciągnij dane'}
              </button>
            </div>
            {busy && (
              <div className="text-[12px] text-ink-500 italic">
                ⏳ OCR + analiza AI mogą zająć 30–90 sekund dla dużych operatów. Nie zamykaj okna.
              </div>
            )}
          </form>
        )}

        {preview && (
          <div className="p-5 grid gap-4">
            <div className="flex items-center justify-between">
              <div className="text-[13px] font-semibold text-ink-900">
                Zatwierdź dane do importu — każdą sekcję osobno
              </div>
              <div className="text-[11px] text-ink-500">
                {preview.provider}{preview.model ? ' · ' + preview.model : ''}
              </div>
            </div>

            <div className="text-[11px] p-2 bg-amber-50 border border-amber-200 rounded text-amber-800">
              ⚠ AI nie jest nieomylne. Sprawdź każdą sekcję — odznacz checkbox jeśli wartości są błędne
              (pominięte pole łatwiej uzupełnić ręcznie niż poprawiać złe dane).
            </div>

            {/* SECTION: Dane ujęcia */}
            <section className="border border-ink-200 rounded-md overflow-hidden">
              <header className="bg-ink-50 px-3 py-2 flex items-center gap-2 border-b border-ink-200">
                <input id="acc-intake" type="checkbox" checked={acceptIntake}
                       onChange={e => setAcceptIntake(e.target.checked)}/>
                <label htmlFor="acc-intake" className="text-[13px] font-semibold cursor-pointer">
                  Dane ujęcia
                </label>
                <span className="text-[11px] text-ink-500 ml-auto">
                  {intakeFieldCount(preview)} pól wyciągniętych
                </span>
              </header>
              <div className={'p-3 text-[12px] grid grid-cols-2 gap-x-4 gap-y-1 ' + (acceptIntake ? '' : 'opacity-40')}>
                <Row label="Nazwa" value={preview.name}/>
                <Row label="Q [m³/h]" value={preview.qM3h}/>
                <Row label="Miejscowość" value={preview.town}/>
                <Row label="Powiat" value={preview.county}/>
                <Row label="Województwo" value={preview.voivodeship}/>
                <Row label="Współrzędne" value={preview.latitude && preview.longitude
                    ? `${preview.latitude.toFixed(5)}, ${preview.longitude.toFixed(5)}` : null}/>
                <Row label="JCWPd" value={preview.jcwpdCode
                    ? `${preview.jcwpdCode}${preview.jcwpdName ? ' (' + preview.jcwpdName + ')' : ''}` : null}/>
                <Row label="Region wodny / dorzecze" value={
                    preview.regionWodny || preview.dorzecze
                      ? [preview.regionWodny, preview.dorzecze].filter(Boolean).join(' · ') : null}/>
                <Row label="Zatw. zasoby [m³/h]" value={preview.approvedResourceM3h}/>
                <Row label="Sygnatura decyzji" value={preview.approvedResourceDecisionRef}/>
              </div>
            </section>

            {/* SECTION: Otwory */}
            {preview.boreholes.length > 0 && (
              <div className="grid gap-3">
                <div className="text-[12px] font-semibold text-ink-700">
                  Otwory ({preview.boreholes.length}) — zatwierdź per otwór i per sekcja
                </div>
                {preview.boreholes.map((b, i) => (
                  <BoreholeSection
                    key={i}
                    index={i}
                    borehole={b}
                    acceptance={boreholeAcc[i] ?? { basic: false, construction: false, hydraulics: false, layers: false }}
                    onAcceptanceChange={(next) => {
                      const arr = [...boreholeAcc];
                      arr[i] = next;
                      setBoreholeAcc(arr);
                    }}
                  />
                ))}
              </div>
            )}

            {preview.sourceTextPreview && (
              <details className="text-[11px]">
                <summary className="cursor-pointer text-ink-500 hover:text-ink-700">
                  Podgląd surowego tekstu z dokumentu (do weryfikacji)
                </summary>
                <div className="mt-2 p-3 bg-ink-50 rounded font-mono text-[10px] max-h-48 overflow-auto whitespace-pre-wrap">
                  {preview.sourceTextPreview}
                </div>
              </details>
            )}

            {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}

            <div className="flex justify-end gap-2 pt-2 border-t border-ink-100 sticky bottom-0 bg-white">
              <button type="button" onClick={reset} className="btn btn-secondary">
                <Icon name="x" className="w-3.5 h-3.5"/> Wgraj inny plik
              </button>
              <button type="button" onClick={apply} className="btn btn-primary">
                <Icon name="check" className="w-3.5 h-3.5"/>
                Zastosuj zaznaczone do kreatora
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function intakeFieldCount(d: ExtractedIntakeData): number {
  return [d.name, d.town, d.county, d.voivodeship, d.latitude, d.longitude, d.qM3h,
    d.jcwpdCode, d.regionWodny, d.dorzecze, d.approvedResourceM3h].filter(v => v != null && v !== '').length;
}

function filterBorehole(b: ExtractedBoreholeData, acc: BoreholeAcceptance | undefined): ExtractedBoreholeData | null {
  if (!acc || !acc.basic) return null;
  const out: ExtractedBoreholeData = { ...b };
  if (!acc.construction) {
    out.housingTypeName = null;
    out.housingDiameterMm = null;
    out.housingDepth = null;
    out.housingCover = null;
    out.housingSteelLadderDiameterMm = null;
    out.housingHydraulicFittings = null;
    out.filterFromDepth = null;
    out.filterToDepth = null;
    out.filterType = null;
    out.filterDiameterMm = null;
    out.pumpType = null;
    out.pumpModel = null;
    out.pumpInstallDepth = null;
    out.pumpDeliveryPipeDiameterMm = null;
    out.pumpMaxCapacityM3h = null;
    out.meterType = null;
    out.meterModel = null;
    out.meterAccuracy = null;
    out.meterLocation = null;
  }
  if (!acc.hydraulics) {
    out.pumpingTestQM3h = null;
    out.pumpingTestDrawdownM = null;
    out.pumpingTestSpecificCapacity = null;
    out.pumpingTestDurationH = null;
    out.permissibleDrawdownM = null;
    out.permeabilityKMs = null;
    out.transmissivityTM2s = null;
  }
  if (!acc.layers) {
    out.layers = [];
  }
  return out;
}

function BoreholeSection({ index, borehole, acceptance, onAcceptanceChange }: {
  index: number;
  borehole: ExtractedBoreholeData;
  acceptance: BoreholeAcceptance;
  onAcceptanceChange: (next: BoreholeAcceptance) => void;
}) {
  const set = (key: keyof BoreholeAcceptance, value: boolean) =>
    onAcceptanceChange({ ...acceptance, [key]: value });

  const hasConstruction = borehole.housingTypeName != null || borehole.filterFromDepth != null
    || borehole.pumpType != null || borehole.meterType != null;
  const hasHydraulics = borehole.pumpingTestQM3h != null || borehole.permeabilityKMs != null
    || borehole.transmissivityTM2s != null;
  const hasLayers = borehole.layers && borehole.layers.length > 0;

  return (
    <div className="border border-ink-200 rounded-md overflow-hidden">
      <header className="bg-ink-50 px-3 py-2 flex items-center gap-2 border-b border-ink-200">
        <input id={`acc-bh-${index}`} type="checkbox" checked={acceptance.basic}
               onChange={e => set('basic', e.target.checked)}/>
        <label htmlFor={`acc-bh-${index}`} className="text-[13px] font-semibold cursor-pointer">
          Otwór {index + 1}: <span className="font-mono">{borehole.designation ?? '?'}</span>
          {borehole.function && (
            <span className="ml-2 text-[10px] text-ink-500 font-normal">
              ({translateFunction(borehole.function)})
            </span>
          )}
        </label>
      </header>
      <div className={'p-3 grid gap-3 text-[12px] ' + (acceptance.basic ? '' : 'opacity-40 pointer-events-none')}>
        <div className="grid grid-cols-3 gap-x-4 gap-y-1">
          <Row label="Głębokość [m]" value={borehole.depth}/>
          <Row label="Średnica [mm]" value={borehole.diameterMm}/>
          <Row label="Wydajność Q [m³/h]" value={borehole.yieldM3h}/>
          <Row label="Rok wykonania" value={borehole.yearCompleted}/>
          <Row label="Śr. pobór [m³/d]" value={borehole.avgDailyIntakeM3}/>
          <Row label="Współrzędne" value={borehole.latitude && borehole.longitude
              ? `${borehole.latitude.toFixed(5)}, ${borehole.longitude.toFixed(5)}` : null}/>
        </div>

        {hasConstruction && (
          <SubSection title="Konstrukcja (obudowa, filtr, pompa, wodomierz)"
                      checked={acceptance.construction} onChange={v => set('construction', v)}>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <Row label="Typ obudowy" value={borehole.housingTypeName}/>
              <Row label="Obudowa Ø [mm]" value={borehole.housingDiameterMm}/>
              <Row label="Obudowa głębokość [m]" value={borehole.housingDepth}/>
              <Row label="Drabinka Ø [mm]" value={borehole.housingSteelLadderDiameterMm}/>
              <Row label="Filtr zakres [m]" value={borehole.filterFromDepth != null && borehole.filterToDepth != null
                  ? `${borehole.filterFromDepth}–${borehole.filterToDepth}` : null}/>
              <Row label="Filtr typ" value={borehole.filterType}/>
              <Row label="Filtr Ø [mm]" value={borehole.filterDiameterMm}/>
              <Row label="Pompa" value={borehole.pumpType
                  ? `${borehole.pumpType}${borehole.pumpModel ? ' · ' + borehole.pumpModel : ''}` : null}/>
              <Row label="Pompa głęb. instal. [m]" value={borehole.pumpInstallDepth}/>
              <Row label="Pompa max [m³/h]" value={borehole.pumpMaxCapacityM3h}/>
              <Row label="Wodomierz" value={borehole.meterType
                  ? `${borehole.meterType}${borehole.meterModel ? ' · ' + borehole.meterModel : ''}` : null}/>
              <Row label="Klasa wodomierza" value={borehole.meterAccuracy}/>
            </div>
          </SubSection>
        )}

        {hasHydraulics && (
          <SubSection title="Hydraulika (próbne pompowanie, k, T)"
                      checked={acceptance.hydraulics} onChange={v => set('hydraulics', v)}>
            <div className="grid grid-cols-3 gap-x-4 gap-y-1">
              <Row label="Q [m³/h]" value={borehole.pumpingTestQM3h}/>
              <Row label="Depresja s [m]" value={borehole.pumpingTestDrawdownM}/>
              <Row label="q jedn. [m³/(h·m)]" value={borehole.pumpingTestSpecificCapacity}/>
              <Row label="Czas [h]" value={borehole.pumpingTestDurationH}/>
              <Row label="Dop. depresja [m]" value={borehole.permissibleDrawdownM}/>
              <Row label="k [m/s]" value={borehole.permeabilityKMs}/>
              <Row label="T [m²/s]" value={borehole.transmissivityTM2s}/>
            </div>
          </SubSection>
        )}

        {hasLayers && (
          <SubSection title={`Profil litologiczny (${borehole.layers.length} warstw)`}
                      checked={acceptance.layers} onChange={v => set('layers', v)}>
            <table className="table-hw text-[11px]">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Od [m]</th>
                  <th style={{ width: 80 }}>Do [m]</th>
                  <th style={{ width: 80 }}>Symbol</th>
                  <th>Opis</th>
                </tr>
              </thead>
              <tbody>
                {borehole.layers.map((l, j) => (
                  <tr key={j}>
                    <td className="font-mono">{l.fromDepth ?? '—'}</td>
                    <td className="font-mono">{l.toDepth ?? '—'}</td>
                    <td className="font-mono">{l.symbol ?? '—'}</td>
                    <td>{l.description ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-[10px] text-ink-400 mt-1">
              ℹ Symbol litologii zostanie dopasowany do katalogu (admin · Typy warstw). Symbole nieznalezione
              zostaną dodane jako warstwa bez powiązania — można uzupełnić w edytorze otworu.
            </div>
          </SubSection>
        )}
      </div>
    </div>
  );
}

function SubSection({ title, checked, onChange, children }: {
  title: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-ink-100 rounded">
      <header className="px-2.5 py-1.5 flex items-center gap-2 bg-ink-50/50 cursor-pointer">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}/>
        <span className="text-[12px] font-semibold text-ink-700">{title}</span>
      </header>
      <div className={'px-2.5 py-2 ' + (checked ? '' : 'opacity-40 pointer-events-none')}>
        {children}
      </div>
    </div>
  );
}

function translateFunction(f: string): string {
  if (f === 'PRODUCTION') return 'eksploatacyjny';
  if (f === 'OBSERVATION') return 'obserwacyjny';
  if (f === 'REPLACEMENT') return 'zastępczy';
  if (f === 'INACTIVE') return 'nieczynny';
  return f;
}

function Row({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value == null || value === '') {
    return (
      <div className="flex items-baseline gap-2">
        <span className="text-ink-400">{label}:</span> <span className="text-ink-400 italic">—</span>
      </div>
    );
  }
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-ink-500">{label}:</span>
      <span className="text-ink-800 font-semibold">{value}</span>
    </div>
  );
}
